import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Link as RouterLink } from 'react-router-dom'
import {
  LayoutDashboard, Building2, Package, ClipboardList, Settings as SettingsIcon,
  LogOut, Menu, X, ChevronRight, Plus, FlaskConical, UserPlus, ShoppingCart, Map,
  Layers, Eye, BarChart3, LifeBuoy, BookOpen, TrendingUp, UserCog, KeyRound, ShieldAlert, Link2, AlertTriangle
} from 'lucide-react'

const STAFF_NAV = [
  { path: '/',                          icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/admin/setup-guide',         icon: BookOpen,        label: 'Setup Guide' },
  { path: '/admin/client-groups',       icon: Layers,          label: 'Client Groups' },
  { path: '/companies',                 icon: Building2,       label: 'Companies' },
  { path: '/products',                  icon: Package,         label: 'Products' },
  { path: '/admin/product-visibility',  icon: Eye,             label: 'Product Visibility' },
  { path: '/orders',                    icon: ClipboardList,   label: 'Orders' },
  { path: '/admin/reports',             icon: BarChart3,       label: 'ReportiQ' },
  { path: '/admin/import-clients',      icon: UserPlus,        label: 'Import Clients' },
  { path: '/admin/team',                icon: UserCog,         label: 'Team' },
  { path: '/admin/preview-links',       icon: Link2,           label: 'Preview Links' },
  { path: '/admin/account-health',      icon: AlertTriangle,   label: 'Account Health' },
  { path: '/admin/director',            icon: ShieldAlert,     label: 'Director Tools' },
  { path: '/settings',                  icon: SettingsIcon,    label: 'Settings' },
]

const CLIENT_NAV = [
  { path: '/place-order', icon: ShoppingCart,  label: 'Place Order' },
  { path: '/catalogue',   icon: Package,       label: 'Products' },
  { path: '/my-orders',   icon: ClipboardList, label: 'My Orders' },
  { path: '/activity',    icon: TrendingUp,    label: 'Activity' },
  { path: '/reports',     icon: BarChart3,     label: 'ReportiQ' },
  { path: '/help',        icon: LifeBuoy,      label: 'How to Order' },
]

export default function Layout({
  children, user, onLogout, brand, role = 'staff',
  companies = [], activeCompanyId = null, onSwitchCompany,
  previewMode = false, onExitPreview,
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const isClient = role !== 'staff'
  const NAV = isClient ? CLIENT_NAV : STAFF_NAV

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }
  const currentLabel = NAV.find(i => isActive(i.path))?.label || 'Order Platform'
  const activeCompany = companies.find(c => c.id === activeCompanyId)

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-brand-dark transform transition-transform duration-200 ease-in-out
        lg:relative lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          <div className="px-6 py-5 border-b border-[color:rgb(var(--brand-sidebar-hover-rgb)/0.2)]">
            <div className="flex items-center gap-3">
              {brand?.logo_url
                ? <img
                    src={brand.logo_url}
                    alt={brand.name || 'Logo'}
                    style={{
                      width: '100%', maxWidth: 180, height: 'auto', display: 'block',
                      mixBlendMode: brand?.logo_on_white ? 'multiply' : 'normal',
                    }}
                  />
                : <img
                    src="/orderiq-logo.png"
                    alt="OrderiQ"
                    className="bg-white/95 rounded-lg p-1.5"
                    style={{ width: '100%', maxWidth: 180, height: 'auto', display: 'block' }}
                  />
              }
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden ml-auto text-[color:rgb(var(--brand-text-rgb)/0.7)] hover:text-[color:var(--brand-text)]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-brand-light text-xs mt-2">{brand?.logo_url ? 'Order Platform' : 'Simpler Ordering. Greater Control.'}</p>
          </div>

          {isClient && companies.length > 1 && (
            <div className="px-3 py-3 border-b border-[color:rgb(var(--brand-sidebar-hover-rgb)/0.2)]">
              <label className="text-[10px] uppercase tracking-wide text-[color:rgb(var(--brand-text-rgb)/0.6)] flex items-center gap-1 mb-1">
                <Map className="w-3 h-3" /> Active site
              </label>
              <select
                value={activeCompanyId || '__all__'}
                onChange={e => onSwitchCompany?.(e.target.value === '__all__' ? null : e.target.value)}
                className="w-full px-2 py-1.5 rounded-md bg-[color:rgb(var(--brand-sidebar-hover-rgb)/0.15)] text-[color:var(--brand-text)] text-sm border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
              >
                <option value="__all__" className="text-gray-900">— All sites ({companies.length}) —</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id} className="text-gray-900">{c.name}</option>
                ))}
              </select>
            </div>
          )}

          {isClient && companies.length === 1 && (
            <div className="px-6 py-2 text-xs text-[color:rgb(var(--brand-text-rgb)/0.7)] border-b border-[color:rgb(var(--brand-sidebar-hover-rgb)/0.2)] truncate">
              {companies[0].name}
            </div>
          )}


          <nav className="px-3 py-4 space-y-1 overflow-y-auto">
            {NAV.filter(item => item.path !== '/reports' && item.path !== '/admin/reports').map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                  ${isActive(item.path)
                    ? 'bg-[color:rgb(var(--brand-sidebar-hover-rgb)/0.22)] text-[color:var(--brand-text)]'
                    : 'text-[color:rgb(var(--brand-text-rgb)/0.7)] hover:bg-[color:rgb(var(--brand-sidebar-hover-rgb)/0.15)] hover:text-[color:var(--brand-text)]'}
                `}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
                {isActive(item.path) && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Link>
            ))}
          </nav>

          {/* ReportiQ card — logo above, "Open ReportiQ" link directly below.
              Positioned just below the last nav item (Settings). */}
          {(() => {
            const reportsPath = role === 'client' ? '/reports' : '/admin/reports'
            const active = isActive(reportsPath)
            return (
              <div className="mx-3 mb-3 rounded-xl overflow-hidden border border-[color:rgb(var(--brand-sidebar-hover-rgb)/0.2)]">
                <div className="bg-white/95 px-3 py-2 flex items-center justify-center">
                  <img src="/reportiq-logo.png" alt="ReportiQ" style={{ maxHeight: 40, width: 'auto', maxWidth: '100%' }} />
                </div>
                <Link
                  to={reportsPath}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-2 px-3 py-2 text-sm font-semibold transition-colors ${
                    active
                      ? 'bg-[color:rgb(var(--brand-sidebar-hover-rgb)/0.22)] text-[color:var(--brand-text)]'
                      : 'text-[color:rgb(var(--brand-text-rgb)/0.85)] hover:bg-[color:rgb(var(--brand-sidebar-hover-rgb)/0.15)] hover:text-[color:var(--brand-text)]'
                  }`}
                >
                  <BarChart3 className="w-4 h-4" />
                  Open ReportiQ
                  <ChevronRight className="w-4 h-4 ml-auto" />
                </Link>
              </div>
            )
          })()}

          <div className="flex-1" />

          <div className="px-3 py-4 border-t border-[color:rgb(var(--brand-sidebar-hover-rgb)/0.2)]">
            <div className="px-3 py-2 text-sm">
              <p className="text-[color:var(--brand-text)] font-medium">{user?.name || user?.email || 'User'}</p>
              {user?.email && user?.name && <p className="text-[color:rgb(var(--brand-text-rgb)/0.5)] text-xs truncate">{user.email}</p>}
              <p className="text-[color:rgb(var(--brand-text-rgb)/0.4)] text-[10px] uppercase tracking-wide mt-1">{role}</p>
            </div>
            <Link
              to="/account"
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[color:rgb(var(--brand-text-rgb)/0.7)] hover:bg-[color:rgb(var(--brand-sidebar-hover-rgb)/0.15)] hover:text-[color:var(--brand-text)] transition-colors w-full"
            >
              <KeyRound className="w-5 h-5" />
              My Account
            </Link>
            <button
              onClick={onLogout}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[color:rgb(var(--brand-text-rgb)/0.7)] hover:bg-[color:rgb(var(--brand-sidebar-hover-rgb)/0.15)] hover:text-[color:var(--brand-text)] transition-colors w-full"
            >
              <LogOut className="w-5 h-5" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {previewMode && (
          <div className="bg-purple-100 border-b border-purple-200 text-purple-900 text-xs px-4 lg:px-8 py-2 flex items-center gap-2">
            <Eye className="w-4 h-4" />
            <span>
              <strong>Preview mode.</strong> You're viewing the client portal exactly as users at this site would see it. Submission buttons are disabled.
            </span>
            <button onClick={onExitPreview} className="ml-auto text-purple-900 hover:underline font-medium">
              Exit preview
            </button>
          </div>
        )}
        {brand?.test_mode && (
          <div className="bg-amber-100 border-b border-amber-200 text-amber-900 text-xs px-4 lg:px-8 py-2 flex items-center gap-2">
            <FlaskConical className="w-4 h-4" />
            <span><strong>Test mode is ON.</strong> Order sends are diverted to{' '}
              <code className="text-amber-800">{brand.test_email || '(no test email set)'}</code>{' '}
              — nothing reaches real recipients. Switch off in Settings when you're ready to go live.
            </span>
          </div>
        )}
        <header className="bg-white border-b border-gray-200 px-4 lg:px-8 py-4 flex items-center gap-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-gray-500 hover:text-gray-700"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex-1 min-w-0">
            {isClient && activeCompany ? (
              <>
                <h2 className="h-display-sm truncate">{activeCompany.name}</h2>
                <p className="text-xs text-gray-500 truncate mt-1">{currentLabel}</p>
              </>
            ) : isClient && companies.length > 1 && !activeCompanyId ? (
              <>
                <h2 className="h-display-sm truncate">All sites</h2>
                <p className="text-xs text-gray-500 truncate mt-1">{currentLabel} · {companies.length} sites combined</p>
              </>
            ) : (
              <h2 className="h-display-sm truncate">
                {currentLabel.split(' ').length > 1 ? (
                  <>
                    {currentLabel.split(' ').slice(0, -1).join(' ')}{' '}
                    <span className="accent">{currentLabel.split(' ').slice(-1)}</span>
                  </>
                ) : currentLabel}
              </h2>
            )}
          </div>
          {!isClient && location.pathname.startsWith('/orders') && (
            <button onClick={() => navigate('/orders/new')} className="btn-primary text-sm">
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">New Order</span>
            </button>
          )}
        </header>

        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          {children}
        </main>

        <footer style={{ textAlign: 'center', padding: '16px', color: '#9ca3af', fontSize: 11, letterSpacing: '0.4px', borderTop: '1px solid #e5e7eb', background: 'white' }}>
          Developed &amp; Coded by <span style={{ color: 'rgb(var(--brand-dark))', fontWeight: 600 }}>Simon Wright</span> 2026
        </footer>
      </div>
    </div>
  )
}
